import { SearchRegistry } from '../types';

export const rydFleetOperationsSearchEntries: SearchRegistry = {
  '/fleet-operations/overview': {
    keywords: [
      'fleet operations',
      'ryd fleet operations',
      'fleet ops',
      'overview',
      'fleet operations overview',
      'fleet team',
      'internal fleet',
      'fleet ops landing',
      'fleet operations dashboard',
    ],
    questions: [
      'Where is the ryd Fleet Operations overview?',
      'How do I open the internal fleet ops dashboard?',
    ],
  },
  '/fleet-operations/savings-calculator': {
    keywords: [
      'savings calculator',
      'fleet savings calculator',
      'cost calculator',
      'savings tool',
      'roi calculator',
      'einsparungsrechner',
      'internal savings calculator',
      'prospect savings',
      'fleet ops calculator',
    ],
    questions: [
      'How do I calculate savings for a prospect fleet?',
      'Where is the internal savings calculator for fleet ops?',
    ],
  },
  '/fleet-operations/contract-tool': {
    keywords: [
      'contract tool',
      'flottenvereinbarung',
      'flottenvertrag',
      'fleet agreement',
      'vertrag erstellen',
      'anlage 1',
      'anlage 2',
      'saeulenpreis',
      'tageslistenpreis',
      'sicherheitsleistung',
      'interne checkliste',
      'onboarding checkliste',
      'vertrag pdf',
      'vertrag docx',
      'contract pdf',
      'contract docx',
      'vertrag per email senden',
    ],
    questions: [
      'How do I create a ryd fleet Flottenvereinbarung?',
      'Wie erstelle ich einen Flottenvertrag?',
      'Where do I set the fuel discount conditions for a fleet contract?',
      'Wie erzeuge ich die interne Checkliste fuer Operations und Finance?',
      'Which annexes end up in the contract document?',
      'Who can use the Contract Tool?',
    ],
    faqs: [
      {
        q: 'What is the Contract Tool and who can use it?',
        a: 'The Contract Tool is the ryd-internal workspace for configuring a ryd fleet Flottenvereinbarung. It is restricted to ryd internal users (userGroup `ryd`, partner type RYD_INTERNAL) with the fleet_operations_admin role — fleet and API partners cannot see it. It produces the customer contract as PDF and DOCX plus an internal Operations/Finance checklist.',
      },
      {
        q: 'How is the contract configured?',
        a: 'In the blocks Sales already knows: contract type (ryd fleet plus or enterprise), A Vertragsparteien, B Fahrzeuganzahl, C Sicherheitsleistung, D Zahlungsart, E Anlage 1 (subscriptions and add-ons), F Anlage 2 (Kraftstoffkonditionen) plus further conditions and the partner/reseller exception. Blocks E and F stay locked until A to D are complete, because the vehicle-tier checks need those numbers.',
      },
      {
        q: 'What happens when a discount cap is exceeded?',
        a: 'The tool warns and records the breach, but never blocks: generation still works and there is no approval workflow. The breach is stored on the contract and printed in the internal checklist, including when the partner/reseller exception hides the warning from Sales.',
      },
      {
        q: 'Which annexes appear in the generated contract?',
        a: 'Annex 1, 2 and 4 always; annex 3 (ryd fleet tap to pay) only when tap to pay is active. Annex 4 always contains exactly one payment section — Kauf auf Rechnung, SEPA B2B-Lastschriftmandat or Kreditkarte. For ryd fleet plus the enterprise condition table and the whole 2b Tageslistenpreis section are removed.',
      },
      {
        q: 'Can the internal checklist be sent to the customer?',
        a: 'No. The checklist carries internal margin data, caps and bank details and is never sendable — the email endpoint rejects it server-side, not just in the UI.',
      },
    ],
  },
  '/fleet-operations/contract-tool/saved': {
    keywords: [
      'saved contracts',
      'saved fleet contracts',
      'gespeicherte vertraege',
      'vertragsuebersicht',
      'contract archive',
      'contract documents',
      'generated contract files',
      'contract pdf download',
      'contract docx download',
      'contract checklist download',
      'email history',
      'email sending history',
      'versandverlauf',
      'e-mail-verlauf',
      'geteilte vertraege',
    ],
    questions: [
      'Where can I find saved fleet contracts?',
      'Wo finde ich gespeicherte Flottenvertraege?',
      'How do I reopen an existing fleet agreement?',
      'Where can I download an older generated contract PDF or DOCX?',
      'Where can I see the email sending history for a contract?',
      'Which contracts have been shared with me?',
    ],
    faqs: [
      {
        q: 'What appears in Saved Contracts?',
        a: 'Saved Contracts lists agreements you created as well as contracts shared with you or your ryd user group. Open a contract to continue working on it; only its creator can change or send it.',
      },
      {
        q: 'Where are generated contract files stored?',
        a: 'Every generated contract PDF, DOCX and internal checklist is archived with its creation timestamp and remains linked to the saved contract. Expand the contract entry in Saved Contracts to see and download the available file versions.',
      },
      {
        q: 'How do I review a contract’s email history?',
        a: 'Open Saved Contracts and use the History action on the relevant contract. The history shows recipients, subject, attachment, sending status and the archived email details for each attempt.',
      },
    ],
  },
  '/fleet-operations/fleet-check/analyse': {
    keywords: [
      'fleet check',
      'fleet check toolkit',
      'tankkarten analyse',
      'fuel card analysis',
      'dkv analyse',
      'uta analyse',
      'abrechnungsanalyse',
      'nachlass analyse',
      'discount analysis',
      'prospect fuel analysis',
      'billing export analysis',
    ],
    questions: [
      'How do I analyze a prospect fuel card export?',
      'Where can I upload DKV or UTA billing files for analysis?',
      'Wie analysiere ich Tankkarten-Abrechnungen von Interessenten?',
      'What is the Fleet Check toolkit?',
      'Which fuel card providers does Fleet Check support?',
      'Who can use Fleet Check?',
    ],
    faqs: [
      {
        q: 'What is Fleet Check and who can use it?',
        a: 'Fleet Check is the ryd-internal sales toolkit for fleet consultants. It is restricted to ryd internal users (userGroup `ryd`, partner type RYD_INTERNAL) — fleet/API partners cannot see it. The toolkit spans tabs A–F: Quick Scan questionnaire, Referenz-Cases, Pilot-Ansprache outreach email, Abrechnungsanalyse (fuel-card billing analysis), Fleet Onboarding, and Preiskalkulator.',
      },
      {
        q: 'How does the Fleet Check billing analysis work?',
        a: 'Pick the card provider, upload a CSV/XLSX billing export (drag & drop, max 20 MB). The backend stores the original, auto-detects the import profile (≥75% column-signature match), normalizes the rows and computes KPIs. Only the most recently uploaded file is evaluated; earlier files are kept as history. Review the column mapping and the parsed-data preview, tick "Daten geprüft", then run the analysis.',
      },
      {
        q: 'Which fuel card providers and formats are supported?',
        a: 'DKV (Invoice, Rosengarten, Total-Cost-Report/Consense — auto-detected) and UTA Edenred (auto-detected) have built-in profiles; Shell, Aral and Logpay are supported via manual column mapping. UTA exports carry no discount data, so discount analysis is hidden for them. You can download a per-provider example template + dummy-data sample (CSV/Excel) from the provider step.',
      },
      {
        q: 'Are the uploaded files and analysis results visible to the customer?',
        a: 'No. Fleet Check is ryd internal only — the uploaded billing files, the parsed transactions and the computed analysis results are visible to ryd internal users (userGroup `ryd`) only, not to the prospect or partner. An analysis can additionally be shared with specific ryd user groups or emails by its owner.',
      },
    ],
  },
  '/fleet-operations/fleet-check/scan': {
    keywords: [
      'quick scan',
      'fleet check scan',
      'consulting questionnaire',
      'fleet fragebogen',
      'bedarfsanalyse',
      'sales discovery questions',
      'fleet check fragebogen',
    ],
    questions: [
      'Where is the Fleet Check quick scan questionnaire?',
      'Welche Fragen stelle ich im Fleet-Check-Erstgespräch?',
      'How is a Quick Scan linked to a customer context?',
      'How do I send the questionnaire to the prospect?',
    ],
    faqs: [
      {
        q: 'What is the Fleet Check Quick Scan?',
        a: 'A 20-question first-conversation questionnaire (5 categories) used as a discovery guide with a prospect. It is saved against the active customer context ("Aktiver Fleet Check"); answers and status are ryd internal only. Opening the tab with a context that already has a saved scan loads its answers automatically.',
      },
      {
        q: 'How do I email the questionnaire to the prospect?',
        a: 'Save the Quick Scan, then go to the Pilot-Ansprache tab — the outreach email (optionally with the fillable questionnaire PDF attached) is sent from there for the active context’s scan. The Quick Scan tab itself only fills + saves the questionnaire and downloads the PDF.',
      },
    ],
  },
  '/fleet-operations/fleet-check-saved-scans': {
    keywords: [
      'saved fleet checks',
      'gespeicherte fleet checks',
      'fleet check list',
      'quick scan list',
      'saved scans',
      'fleet check uebersicht',
      'completed fleet checks',
    ],
    questions: [
      'Where do I see saved Fleet Checks?',
      'Wo finde ich gespeicherte Fleet Checks?',
      'How do I reopen a completed Fleet Check scan?',
    ],
    faqs: [
      {
        q: 'What can I do on the Saved Fleet Checks page?',
        a: 'Search and reopen your team’s saved Quick Scans, open the email-history for a scan, and delete a scan (delete is blocked while it is shared). Reopening loads the scan with its answers and active context. All entries are ryd internal only.',
      },
    ],
  },
  '/fleet-operations/fleet-check/cases': {
    keywords: [
      'reference cases',
      'referenz cases',
      'fleet check cases',
      'customer references',
      'pitch cases',
      'anonymized fleet cases',
    ],
    questions: [
      'Where do I find anonymized fleet reference cases for a pitch?',
      'Welche Referenz-Cases gibt es für den Fleet Check?',
    ],
  },
  '/fleet-operations/fleet-check/outreach': {
    keywords: [
      'pilot outreach',
      'pilot ansprache',
      'fleet check email templates',
      'outreach templates',
      'prospect email',
      'pilot email template',
      'mail vorlage fleet',
    ],
    questions: [
      'Where are the Fleet Check pilot outreach email templates?',
      'Wie spreche ich einen Interessenten für einen Fleet-Pilot an?',
      'How do I send the prospect outreach email?',
      'Where do I see whether the outreach email was delivered or opened?',
      'Where does the sender name come from?',
    ],
    faqs: [
      {
        q: 'How do I send the Fleet Check outreach email to a prospect?',
        a: 'On the Pilot-Ansprache tab, pick a template, adjust the recipient/subject/body and optionally attach the questionnaire PDF, then send. It sends for the active customer context’s saved Quick Scan; if no scan exists yet, finish a Quick Scan first. The sender name/email default to your logged-in ryd portal user (firstName + lastName, email); the title comes from Fleet Check → Einstellungen.',
      },
      {
        q: 'How do I see if the outreach email was delivered or opened?',
        a: 'Open the email-history (Pilot-Ansprache tab, or the Saved Fleet Checks list). It shows one row per send with delivery state, open count, last-opened time and an SMTP timeline. The email content is stored permanently; Mandrill delivery/open stats are cached and refreshed periodically (Mandrill keeps them ~90 days).',
      },
    ],
  },
  '/fleet-operations/fleet-check/onboarding': {
    keywords: [
      'fleet check onboarding',
      'onboarding ableitung',
      'fleet inventory derivation',
      'card inventory',
      'last billing month',
      'inactivity fleet onboarding',
    ],
    questions: [
      'How do I derive a fleet onboarding view from an analysis?',
      'Wie leite ich das Onboarding aus einer Fleet-Check-Analyse ab?',
      'How do I pass the analysed fleet size into the price calculator?',
    ],
    faqs: [
      {
        q: 'What does Fleet Onboarding derive from an analysis?',
        a: 'Pick a computed analysis and it derives onboarding KPIs — active vehicles, driver count, last billing month, period, fuel litres and gross cost — plus preparation notes. Use "Im Preiskalkulator öffnen" to carry the derived driver count into the Preiskalkulator (it pre-seeds the Fahrerplätze).',
      },
    ],
  },
  '/fleet-operations/fleet-check/kalkulator': {
    keywords: [
      'fleet check kalkulator',
      'preiskalkulator',
      'fleet check pricing',
      'savings from analysis',
      'fahrerplätze',
      'pool kfz',
      'abo modell',
      'plus enterprise free',
    ],
    questions: [
      'Where is the Fleet Check price calculator?',
      'How is the ryd fleet price calculated?',
      'How do I seed the calculator with the analysed driver count?',
    ],
    faqs: [
      {
        q: 'How does the Fleet Check Preiskalkulator work?',
        a: 'It configures a live ryd fleet offer: plan (free/plus/enterprise), billing cadence, payment method, fleet size (Fahrerplätze + Pool-Kfz) and add-ons, and shows the monthly/yearly net cost with tier discounts. The driver-seat count can be pre-seeded from a Fleet Onboarding analysis via "Im Preiskalkulator öffnen" (passes `?drivers`/`?pool`); otherwise it defaults and you edit it manually.',
      },
    ],
  },
  '/fleet-operations/fleet-check/settings': {
    keywords: [
      'fleet check settings',
      'fleet check einstellungen',
      'sender profile',
      'analysis module toggles',
      'absenderprofil',
      'fleet check modules',
    ],
    questions: [
      'Where do I edit the Fleet Check sender profile?',
      'How do I toggle which Fleet Check analysis modules are shown?',
      'Where does the email sender name and title come from?',
    ],
    faqs: [
      {
        q: 'What is the Fleet Check sender profile (Absenderprofil)?',
        a: 'The name and title used in the Pilot-Ansprache outreach emails. By default the first and last name of your logged-in ryd portal user are used; you can override them by entering a name here. The title/role is not stored in the ryd portal user account, so it must be set here. The module toggles control which analysis modules are shown in the workspace.',
      },
    ],
  },
  '/fleet-operations/savings-calculator/saved': {
    keywords: [
      'saved calculations',
      'savings history',
      'past calculations',
      'calculator history',
      'saved savings list',
      'fleet ops calculation history',
    ],
    questions: ['Where do I find previously saved fleet savings calculations?'],
  },
  '/fleet-operations/savings-calculator-sessions': {
    keywords: [
      'calculator sessions',
      'savings calculator sessions',
      'homepage calculator sessions',
      'ersparnisrechner sessions',
      'website calculator monitoring',
      'calculator funnel',
      'calculator session list',
      'lead company calculator',
      'acquisition funnel',
      'funnel sessions',
      'fleet leads',
    ],
    questions: [
      'Where do I see homepage savings calculator sessions?',
      'How do I monitor website calculator submissions and provisioning?',
    ],
  },
  '/fleet-operations/savings-calculator/admin/country-pricing': {
    keywords: [
      'country pricing admin',
      'pricing and caps admin',
      'savings calculator commercial settings',
      'country pricing',
      'discount limits admin',
      'approved discount limits',
      'publish price set',
      'activate price set',
      'country assumptions',
      'diesel price per litre',
      'ev price per kwh',
      'source document link',
      'fleet savings pricing config',
    ],
    questions: [
      'Where do I edit country pricing for the savings calculator?',
      'How do I publish a new savings-calculator price set?',
      'How do I change approved discount limits for a country?',
    ],
    faqs: [
      {
        q: 'What does the Country Pricing Admin page change?',
        a: 'It maintains the commercial assumptions used for new fleet savings calculations per country, including energy prices, discounts, fees and approved discount limits. Existing saved calculations keep the assumptions they were created with; only future calculations pick up the new active pricing set.',
      },
    ],
  },
  '/fleet-operations/partner-list': {
    keywords: [
      'fleet partners',
      'partner list',
      'fleet partner list',
      'partners',
      'fleet companies',
      'sponsors',
      'fleet customers',
      'fleet partner directory',
      'partner detail page',
    ],
    questions: [
      'Where do I see all fleet partners?',
      'How do I open a specific fleet partner detail page?',
    ],
  },
  '/fleet-operations/driver-management': {
    keywords: [
      'fleet drivers',
      'driver management',
      'drivers list',
      'fleet driver lookup',
      'fahrer',
      'fleet ops drivers',
      'all drivers',
    ],
    questions: [
      'Where do I see all fleet drivers across partners?',
      'How do I look up a fleet driver by email?',
    ],
  },
  '/fleet-operations/order-management': {
    keywords: [
      'fleet transactions',
      'fleet order management',
      'fleet orders',
      'fleet transaction list',
      'flotten transaktionen',
      'fleet ops orders',
      'cross-partner orders',
    ],
    questions: [
      'Where do I see fleet transactions across partners?',
      'How do I filter fleet orders by sponsor or driver?',
    ],
  },
  '/fleet-operations/fleet-temp-pws': {
    keywords: [
      'temp passwords',
      'temporary passwords',
      'temp pw',
      'fleet temp passwords',
      'driver temp password',
      'temppw viewer',
      'temp password viewer',
      'fleet management temp password viewer',
      'driver welcome password',
    ],
    questions: [
      'Where do I see temporary passwords issued to drivers?',
      'How do I share a temporary password securely?',
    ],
  },
  '/fleet-operations/fleet-virtual-emails': {
    keywords: [
      'virtual emails',
      'virtual email addresses',
      'fleet virtual email',
      'driver virtual email',
      'alias email',
      'virtual mailbox',
      'driver email alias',
    ],
    questions: [
      'What are virtual emails for fleet drivers?',
      'Where do I look up virtual email addresses?',
    ],
  },
  '/fleet-operations/vehicle-activity-log': {
    keywords: [
      'vehicle activity log',
      'vehicle audit log',
      'assignment audit',
      'driver assignment history',
      'who unassigned',
      'vehicle history',
      'license plate history',
      'vehicle deleted',
      'vehicle edited',
      'car pool changes',
      'customer support car change',
      'bulk read-only update',
    ],
    questions: [
      'Who removed a driver from a vehicle?',
      'When was the license plate of a fleet vehicle changed?',
      'Which vehicle changes did customer support or a batch update make for a fleet?',
    ],
    faqs: [
      {
        q: 'How do I find out who unassigned a driver from a vehicle?',
        a: 'Open Vehicle Activity Log, pick the fleet partner, then filter by the license plate or the driver ID and the activity type `Driver Unassigned`. Each row shows who made the change, through which channel, and whether it succeeded; Details show every affected driver.',
      },
    ],
  },
  '/fleet-operations/driver-creation-audit': {
    keywords: [
      'driver creation audit',
      'driver onboarding audit',
      'audit events',
      'onboarding audit log',
      'driver creation events',
      'event severity',
      'final onboarding status',
      'partner id audit',
      'user email audit',
      'account id audit',
      'metadata audit',
    ],
    questions: [
      'Where do I inspect driver onboarding audit events?',
      'How do I filter driver creation audit logs by partner ID or user email?',
      'How do I show only failed onboarding events in fleet operations?',
    ],
    faqs: [
      {
        q: 'How do I surface only failing driver-onboarding events?',
        a: 'Open Driver Creation Audit, choose the date range, then set the event severity filter to `ERROR`. You can further narrow the search by partner ID, user email, user ID, account ID, event name or final onboarding status.',
      },
    ],
  },
  '/fleet-operations/fleet-hp-form': {
    keywords: [
      'hp fleet form',
      'fleet form data',
      'hp form',
      'high-priority form',
      'fleet acquisition form',
      'fleet sign-up form',
      'fleet lead form',
      'hp form submissions',
    ],
    questions: [
      'Where do I see the HP fleet form submissions?',
      'How do I follow up on a fleet form lead?',
    ],
  },
  '/fleet-operations/payment-method-switch': {
    keywords: [
      'payment method switch',
      'switch payment method',
      'payment transition',
      'fleet payment method',
      'sepa direct debit',
      'credit card',
      'sepa to credit card',
      'credit card to sepa',
      'payment method change',
      'fleet billing payment',
      'off-peak transition',
    ],
    questions: [
      'How do I switch a fleet partner payment method?',
      'Where do I start a fleet payment method transition?',
      'How do I switch a fleet from credit card to SEPA?',
    ],
  },
  '/fleet-operations/fleet-suspension': {
    keywords: [
      'fleet suspension',
      'suspend fleet',
      'block fleet',
      'unblock fleet',
      'reactivate fleet',
      'fleet status management',
      'block fleet company',
      'disable fleet payment method',
      'fleet suspension email',
      'fleet suspension audit',
    ],
    questions: [
      'How do I block or unblock a fleet partner?',
      'Where do I suspend a fleet company?',
      'How do I reactivate a suspended fleet?',
    ],
    faqs: [
      {
        q: 'What happens when I block a fleet partner?',
        a: 'The suspension workspace guides you through changing the fleet status, updating the shared payment method, notifying recipients, and recording the completed workflow in the audit history.',
      },
    ],
  },
  '/fleet-operations/fleet-suspension/journeys': {
    keywords: [
      'suspension journeys',
      'fleet suspension history',
      'fleet block history',
      'fleet unblock history',
      'suspension workflow history',
      'failed suspension journey',
      'fleet status audit journey',
      'block unblock audit log',
    ],
    questions: [
      'Where do I see fleet suspension journey history?',
      'How do I find a failed fleet block or unblock workflow?',
      'Where can I open the timeline of a fleet suspension?',
    ],
  },
  '/fleet-operations/fleet-suspension/journeys/:journeyId': {
    keywords: [
      'suspension journey detail',
      'fleet suspension timeline',
      'fleet block journey detail',
      'fleet unblock journey detail',
      'resume failed suspension journey',
      'suspension workflow steps',
      'suspension captured request',
    ],
    questions: [
      'How do I review each step of a fleet suspension journey?',
      'How do I resume a failed fleet suspension journey?',
      'Where do I see technical details for a fleet block workflow?',
    ],
  },
  '/fleet-operations/payment-transitions/:fleetId/:transitionId': {
    keywords: [
      'payment transition detail',
      'transition detail page',
      'sepa transition detail',
      'fleet payment transition status',
      'odoo billing config transition',
    ],
    questions: [
      'Where do I inspect one payment transition in full detail?',
      'How do I see Odoo billing config for a SEPA transition?',
    ],
  },
  '/fleet-operations/payment-transitions': {
    keywords: [
      'payment transitions',
      'transition history',
      'fleet payment transition status',
      'sepa transition status',
      'payment method transition log',
      'fleet payment transition details',
    ],
    questions: [
      'Where do I see payment transition history for a fleet partner?',
      'How do I check if a SEPA switch completed?',
    ],
  },
  '/fleet-operations/partner-billing': {
    keywords: [
      'partner billing',
      'fleet partner billing',
      'billing limits',
      'fleet purchase limits',
      'billing search',
      'sponsor billing',
      'fleet billing status',
      'next invoice run',
      'spending utilisation',
      'billing cycle',
      'partner billing list',
    ],
    questions: [
      'Where do I review partner billing limits?',
      'How do I search fleet partners by billing status?',
      'Where do I see the next invoice run for fleet partners?',
    ],
  },
  '/fleet-operations/partner-billing/analytics': {
    keywords: [
      'partner billing analytics',
      'billing analytics',
      'fleet billing analytics',
      'spending analytics',
      'limit utilisation analytics',
      'billing dashboard',
      'invoice analytics',
    ],
    questions: [
      'Where do I analyse partner billing usage?',
      'How do I review billing utilisation across fleet partners?',
    ],
  },
  '/fleet-operations/partner-billing/fleet-invoice-data-hub': {
    keywords: [
      'fleet invoice data hub',
      'invoice data hub',
      'invoice mirror',
      'merged invoices',
      'odoo bigquery invoices',
      'invoice sync',
      'invoice watermark',
      'fleet invoice mirror',
      'unresolved invoices',
      'invoice backfill',
      'fct_merged_invoices',
      'odoo invoicing dataset',
      'credit note mirror',
      'aggregated invoice sync',
    ],
    questions: [
      'Where do I check the Odoo invoice BigQuery mirror?',
      'How do I run a manual invoice sync or backfill?',
      'Where do I find unresolved mirrored invoices?',
      'How do I browse mirrored fleet invoices across sponsors?',
      'Which partner invoices are available in the Fleet Invoice Data Hub?',
    ],
    faqs: [
      {
        q: 'Where do I operate the Odoo → BigQuery invoice mirror?',
        a: 'Open Fleet Operations → Partner Billing → Fleet Invoice Data Hub. Sync Ops shows watermark, serving flags, and volume; Invoice Explorer searches fct_merged_invoices across sponsors. Manual runs need fleet_operations_agent or fleet_operations_admin.',
      },
      {
        q: 'Which partner invoices are available in the Fleet Invoice Data Hub?',
        a: 'The hub covers Fleet and API partners that receive invoices directly from ryd. It intentionally excludes B2B2C end-customer invoice streams such as Mercedes, BMW, and Škoda, because the end customers—not the partners—receive those invoices.',
      },
    ],
  },
  '/fleet-operations/partner-billing/:sponsorId': {
    keywords: [
      'partner billing detail',
      'billing detail',
      'sponsor billing detail',
      'fleet company billing detail',
      'driver billing usage',
      'driver utilisation',
      'spending vs limit',
      'refresh billing limits',
      'full billing refresh',
    ],
    questions: [
      'Where do I inspect billing details for one fleet partner?',
      'How do I review drivers and spending for a fleet billing sponsor?',
      'How do I refresh billing limit data for one partner?',
    ],
  },
  '/fleet-operations/fleet-marketing-funnel-sessions': {
    keywords: [
      'marketing funnel',
      'fleet funnel',
      'funnel sessions',
      'fleet marketing funnel',
      'acquisition funnel',
      'fleet leads',
      'funnel analytics',
      'session list funnel',
    ],
    questions: [
      'Where do I see fleet marketing funnel sessions?',
      'How do I analyse fleet funnel drop-off?',
    ],
  },
  '/fleet-operations/fleet-checkout/statistics': {
    keywords: [
      'fleet checkout statistics',
      'checkout funnel',
      'checkout conversion',
      'funnel v5',
      'checkout leads',
      'checkout drop-off',
      'plus revenue',
    ],
    questions: [
      'How many visitors completed the fleet checkout?',
      'Where do I see fleet checkout conversion per day?',
    ],
  },
  '/fleet-operations/fleet-checkout/sessions': {
    keywords: [
      'fleet checkout sessions',
      'fleet checkout signups',
      'checkout sessions',
      'abandoned checkout',
      'checkout drafts',
      'checkout lead email',
      'funnel v5 sessions',
    ],
    questions: [
      'Where do I find an abandoned fleet checkout?',
      'What did a visitor enter on the fleet checkout?',
    ],
  },
  '/fleet-operations/fleet-checkout/signup-orders': {
    keywords: [
      'fleet checkout signup orders',
      'checkout signup order',
      'checkout provisioning',
      'retry provisioning',
      'account setup',
      'fleet checkout orders',
    ],
    questions: [
      'Where do I see fleet checkout signup orders?',
      'How do I retry a failed fleet checkout account setup?',
    ],
  },
  '/fleet-operations/fleet-checkout/overview': {
    keywords: [
      'fleet checkout overview',
      'fleet checkout v5',
      'public fleet signup',
      'fleet checkout pages',
      'fleet checkout permissions',
    ],
    questions: [
      'Which pages exist for the fleet checkout?',
      'Which role do I need to retry a fleet checkout account setup?',
    ],
  },
  '/fleet-operations/fleet-checkout/how-it-works': {
    keywords: [
      'how the fleet checkout works',
      'fleet checkout glossary',
      'checkout session',
      'lead vs signup order',
      'fleet checkout names',
    ],
    questions: [
      'What is a checkout session?',
      'What is the difference between a lead and a signup order?',
      'When does a new fleet checkout session start?',
    ],
  },
  '/fleet-operations/fleet-checkout/callbacks': {
    keywords: [
      'fleet checkout callbacks',
      'callback requests',
      'call back',
      'sales callback',
      'checkout phone requests',
    ],
    questions: [
      'Where do I see callback requests from the fleet checkout?',
      'How do I close a fleet checkout callback request?',
    ],
  },
  '/fleet-operations/spend-analytics': {
    keywords: [
      'driver spend analytics',
      'spend analytics',
      'fleet spend analytics',
      'driver spending',
      'fleet spend reporting',
      'spend per driver',
      'spend per sponsor',
      'fleet spend chart',
    ],
    questions: [
      'Where do I see driver spend analytics for a fleet?',
      'How do I analyse fleet driver spending patterns?',
    ],
  },
  '/fleet-operations/knowledge-base': {
    keywords: [
      'fleet knowledge base',
      'knowledge base',
      'fleet kb',
      'wissensdatenbank',
      'fleet operations docs',
      'kb overview',
      'fleet ops kb',
      'internal fleet documentation',
    ],
    questions: [
      'Where is the fleet operations knowledge base?',
      'How do I find internal fleet docs?',
    ],
  },
  '/fleet-operations/knowledge-base/billing-cycles': {
    keywords: [
      'billing cycles',
      'fleet billing cycle',
      'billing schedule',
      'rechnungszyklus',
      'invoicing cycle',
      'invoice schedule',
      'monthly billing',
      'biweekly billing',
      'cycle close date',
    ],
    questions: [
      'How do fleet billing cycles work?',
      'When does a billing cycle close for a fleet partner?',
    ],
  },
  '/fleet-operations/knowledge-base/fleet-partner-onboarding': {
    keywords: [
      'fleet partner onboarding',
      'onboarding guide',
      'partner onboarding',
      'fleet onboarding',
      'go-live checklist',
      'fleet onboarding steps',
      'fleet kickoff',
      'fleet implementation guide',
    ],
    questions: [
      'How do I onboard a new fleet partner?',
      'Where is the fleet partner onboarding guide?',
    ],
  },
  '/fleet-operations/knowledge-base/nfc-readiness': {
    keywords: [
      'nfc readiness',
      'nfc ready',
      'nfc settings complete',
      'partner nfc status',
      'fleet partner nfc check',
      'live nfc readiness check',
      'nfc payments',
      'tap to pay',
      'allnet',
      'virtual card',
      'virtual fleet card',
      'dipocket',
      'nfc feature configuration',
      'nfc payment flow',
      'card tokenization',
      'card missing',
      'card inactive',
      'driver nfc status',
      'dipocket card issuer',
      'odoo fleet billing configuration',
      'mailchimp audience automation',
      'fleet marketing data',
      'nfc manager notifications',
      'nfc driver notifications',
      'HASEV',
      'TTPENABLDT',
    ],
    questions: [
      'How do I make a fleet NFC-ready?',
      'Are the NFC settings for a fleet partner complete?',
      'Is NFC ready for a specific fleet partner?',
      'How do I run the live NFC readiness check?',
      'Which sponsor and driver checks determine NFC readiness?',
      'Is the fleet enabled for NFC marketing notifications?',
      'How do drivers get a virtual card for tap to pay?',
      'Why can a driver not pay with NFC at the terminal?',
    ],
    faqs: [
      {
        q: 'How can I check whether a fleet partner is NFC-ready?',
        a: 'Open the NFC Readiness Guide and use the Run Check tab to select the fleet partner. The live check validates sponsor configuration, Odoo company and billing setup, marketing automation, and every checked driver’s name and virtual-card status. ryd internal users can also ask the portal chatbot “Is NFC ready for <partner name>?” for a configuration summary and a prefilled link to the full check.',
      },
      {
        q: 'What must be complete for a fleet partner to be fully NFC-ready?',
        a: 'All sponsor and company checks must be verifiably complete, and every driver must have a complete name and an ACTIVE virtual card. A warning, a missing or inactive card, a missing driver name, a failed account lookup, or a truncated result means readiness is not fully verified.',
      },
      {
        q: 'What does the NFC readiness check validate on the sponsor?',
        a: 'It checks the NFC payment issuer, PSP methods, sponsor lifecycle, invoicing, DiPocket partner profile, sponsor status, the linked Odoo company record and address, and the Odoo fleet billing configuration including the penalty flag.',
      },
      {
        q: 'What do Card Missing, Card Inactive, and Name Missing mean?',
        a: 'Card Missing means the driver account has no virtual card; Card Inactive means a virtual card exists but is not ACTIVE; Name Missing means the driver lacks a complete first or last name required for card issuing. Use the remediation actions in the live result or on the partner details page.',
      },
      {
        q: 'What does the Marketing category in the NFC readiness check validate?',
        a: 'It confirms that Mailchimp Audience Export is enabled so fleet managers and drivers enter the NFC communication audiences. It also validates the Has EV Vehicles segmentation value and shows whether the Tap to Pay activation date is manually set or derived from nfcPayment feature history. Marketing readiness does not block virtual-card provisioning, but a disabled audience export means NFC notifications are not ready.',
      },
    ],
    urlParameters: ['sponsorId'],
  },
  '/fleet-operations/partner-fuel-cards/orders': {
    keywords: [
      'fuel card orders',
      'partner fuel cards',
      'aral fuel card',
      'bmw fuel card',
      'order status',
      'reveal card number',
      'mark shipped',
      'retry order',
      'set cancelled',
      'reject order',
      'tim id',
    ],
    questions: [
      'Where do I see partner fuel-card orders?',
      'How do I mark a fuel card as shipped?',
      'How do I reveal a full fuel-card number?',
    ],
  },
  '/fleet-operations/partner-fuel-cards/sftp-files': {
    keywords: [
      'sftp files',
      'sftp browser',
      'cloud ftp',
      'bp sftp',
      'aral sftp',
      'outbound folder',
      'inbound folder',
      'archive folder',
      'request file',
      'response file',
      'redacted file',
    ],
    questions: [
      'Where can I see the files on the BP SFTP server?',
      'Did BP write a response file into the inbound folder?',
    ],
    faqs: [
      {
        q: 'Who can browse the fuel-card SFTP folders?',
        a: 'ryd internal users with both fuel_card_ops and fuel_card_sftp_viewer. The page lists outbound, inbound and the two archive folders, read-only; files open redacted (PINs replaced, card numbers masked to the last four digits) and every listing and view is audited.',
      },
    ],
  },
  '/fleet-operations/partner-fuel-cards/files': {
    keywords: [
      'fuel card files',
      'provider files',
      's2s files',
      'bp files',
      'sequence number',
      'build file now',
      'cancel only file',
      'poll now',
      'rejected by provider',
      '1002',
      'redacted csv',
    ],
    questions: [
      'Where are the S2S files sent to BP?',
      'How do I record a file BP rejected by e-mail?',
      'How do I set the next S2S sequence number?',
    ],
  },
  '/fleet-operations/partner-fuel-cards/ops-queue': {
    keywords: [
      'fuel card ops queue',
      'ops items',
      'held row',
      'unmatched row',
      'reconciliation required',
      'not picked up',
      'unexpected stop',
      'row error',
    ],
    questions: [
      'Which fuel-card issues need an Ops decision?',
      'What do I do with a row held by BP?',
    ],
  },
  '/fleet-operations/partner-fuel-cards/manual-import': {
    keywords: [
      'fuel card manual import',
      'leipzig fallback',
      'csv import fuel cards',
      'batch import orders',
      'manual orders',
    ],
    questions: [
      'How do I import fuel-card orders from a CSV?',
      'How do I create fuel-card orders without TIM?',
    ],
  },
  '/fleet-operations/partner-fuel-cards/programs': {
    keywords: [
      'fuel card programs',
      'aral program',
      'permission groups',
      'emergency block',
      'fuel card configuration',
    ],
    questions: [
      'Which fuel-card programs are configured?',
      'Which permission groups does the BMW ARAL program allow?',
    ],
  },
};
